import {
  GET_REPORT_FORMS_URL,
  GET_REPORT_MAIN_SECTIONS_URL,
  GET_REPORT_MANAGEMENT_LOOKUPS_URL,
  GET_REPORT_SECTION_QUESTIONS_URL,
  GET_REPORT_SUB_SECTIONS_URL,
  GET_REPORT_TEMPLATE_URL,
} from "@/utils/constants/constants";
import axios from "axios";

const get_error_message = (error, fallback_message) => {
  const response_message = error?.response?.data?.message;
  const resolved_message = response_message || fallback_message;
  return resolved_message;
};

const get_response_rows = (response) => {
  const rows = response?.data?.data || [];
  return rows;
};

const build_request_config = (headers, params) => {
  const config = {
    headers,
    params,
  };
  return config;
};

const close_loader_if_present = (component, loader) => {
  if (!loader) {
    return Promise.resolve(null);
  }
  return component.close_loader(loader);
};

/**
 * Data-loading and template-hydration methods for the report builder.
 */
export default {
  load_lookups() {
    this.loading_lookups = true;
    return this.show_loader("Loading report builder...")
      .then((loader) => axios.get(
        GET_REPORT_MANAGEMENT_LOOKUPS_URL,
        build_request_config(this.headers, { project_id: this.project_id })
      )
        .then((response) => this.handle_lookup_success(response))
        .catch((error) => this.handle_loading_error(error, "Failed to load report metadata"))
        .finally(() => this.finalize_lookup_loading(loader)));
  },
  handle_lookup_success(response) {
    const lookup_data = response?.data?.data || this.lookup_data;
    this.lookup_data = lookup_data;
    this.initialize_defaults();
    if (this.get_route_template_id()) {
      return this.load_template();
    }
    return this.load_forms();
  },
  finalize_lookup_loading(loader) {
    this.loading_lookups = false;
    return close_loader_if_present(this, loader);
  },
  load_template() {
    this.loading_template = true;
    return axios.get(
      GET_REPORT_TEMPLATE_URL,
      build_request_config(this.headers, {
        template_id: this.get_route_template_id(),
      })
    )
      .then((response) => this.handle_template_success(response))
      .catch((error) => this.handle_loading_error(error, "Failed to load the template."))
      .finally(() => {
        this.loading_template = false;
      });
  },
  handle_template_success(response) {
    const template = response?.data?.template;
    this.apply_template(template);
    return this.load_forms();
  },
  apply_template(template) {
    this.editing_template_id = template.id;
    this.selected_report_type_id =
      this.normalize_id(template.report_type?.id) ||
      this.get_default_report_type_id();
    this.save_as_template = true;
    this.template_name = template.name || "";
    this.template_description = template.description || "";
    this.selected_date_dimension_id = this.normalize_id(
      template.date_dimension?.id
    );
    this.selected_date_from = template.date_from || "";
    this.selected_date_to = template.date_to || "";
    this.selected_field_ids = (template.columns || [])
      .map((column) => column.report_field?.id)
      .map((field_id) => this.normalize_id(field_id))
      .filter(Boolean);
    this.field_display_labels = this.hydrate_template_field_display_labels(
      template.columns
    );
    this.selected_entity_type_ids = (template.entity_type_assignments || [])
      .map((assignment) => assignment.entity_type?.id)
      .map((entity_type_id) => this.normalize_id(entity_type_id))
      .filter(Boolean);
    this.use_project_scope =
      template.scope_type?.internal_name !== "selected_entity_types";
    this.selected_questions = this.hydrate_template_questions(template);
    this.sync_selected_fields(this.selected_report_type_internal_name);
    this.sync_selected_date_dimension();
  },
  handle_report_type_change() {
    const report_type_internal_name = this.selected_report_type_internal_name;
    this.sync_selected_fields(report_type_internal_name);
    this.sync_selected_date_dimension();
    this.sync_question_metric_selections();
    this.reset_hierarchy();
    if (!this.is_question_scope_report) {
      this.selected_questions = [];
      this.available_forms = [];
      return Promise.resolve(null);
    }
    return this.load_forms();
  },
  handle_scope_change() {
    this.reset_hierarchy();
    this.prune_selected_questions();
    return this.load_forms();
  },
  load_forms() {
    if (!this.should_load_forms()) {
      this.available_forms = [];
      return Promise.resolve(null);
    }
    this.loading_forms = true;
    return axios.get(
      GET_REPORT_FORMS_URL,
      build_request_config(this.headers, this.build_form_params())
    )
      .then((response) => {
        this.available_forms = get_response_rows(response);
      })
      .catch((error) => this.handle_collection_error("available_forms", error, "Failed to load report forms"))
      .finally(() => {
        this.loading_forms = false;
      });
  },
  should_load_forms() {
    if (!this.is_question_scope_report || !this.project_id) {
      return false;
    }
    if (!this.is_profile_scope && !this.use_project_scope) {
      return Boolean(this.selected_entity_type_ids.length);
    }
    return true;
  },
  load_main_sections() {
    this.reset_main_section_state();
    if (!this.selected_form_id) {
      return Promise.resolve(null);
    }
    this.loading_main_sections = true;
    return axios.get(
      GET_REPORT_MAIN_SECTIONS_URL,
      build_request_config(this.headers, { form_id: this.selected_form_id })
    )
      .then((response) => {
        this.main_sections = get_response_rows(response);
      })
      .catch((error) => this.handle_collection_error("main_sections", error, "Failed to load form sections"))
      .finally(() => {
        this.loading_main_sections = false;
      });
  },
  load_sub_sections() {
    this.reset_sub_section_state();
    if (!this.selected_main_section_id) {
      return Promise.resolve(null);
    }
    this.loading_sub_sections = true;
    return axios.get(
      GET_REPORT_SUB_SECTIONS_URL,
      build_request_config(this.headers, {
        form_main_section_id: this.selected_main_section_id,
      })
    )
      .then((response) => {
        this.sub_sections = get_response_rows(response);
      })
      .catch((error) => this.handle_collection_error("sub_sections", error, "Failed to load sub-sections"))
      .finally(() => {
        this.loading_sub_sections = false;
      });
  },
  load_questions() {
    this.reset_question_state();
    if (!this.selected_sub_section_id) {
      return Promise.resolve(null);
    }
    this.loading_questions = true;
    return axios.get(
      GET_REPORT_SECTION_QUESTIONS_URL,
      build_request_config(this.headers, {
        form_section_id: this.selected_sub_section_id,
      })
    )
      .then((response) => {
        this.question_list = get_response_rows(response);
      })
      .catch((error) => this.handle_collection_error("question_list", error, "Failed to load questions"))
      .finally(() => {
        this.loading_questions = false;
      });
  },
  reset_main_section_state() {
    this.selected_main_section_id = "";
    this.selected_sub_section_id = "";
    this.sub_sections = [];
    this.question_list = [];
  },
  reset_sub_section_state() {
    this.selected_sub_section_id = "";
    this.question_list = [];
  },
  reset_question_state() {
    this.question_list = [];
  },
  handle_collection_error(collection_name, error, fallback_message) {
    Reflect.set(this, collection_name, []);
    this.handle_loading_error(error, fallback_message);
  },
  handle_loading_error(error, fallback_message) {
    const message = get_error_message(error, fallback_message);
    this.show_error(message);
  },
};
